import type { NextMeeting } from '../api/client'

/*
  UI SPEC §3 — the living agenda.

  "A scheduled meeting with an invisible agenda is a calendar reminder. A meeting that
   shows Friday · 4 items · board has prepped 3 is an appointment he doesn't want to miss."

  So the dot row is the point. It shows the container filling: one dot per item, filled
  once the board has prepped it. An empty meeting says so plainly rather than pretending,
  because §9's fallback is a feature and hiding it would make the week look wasted.
*/

export function MeetingChip({
  next, onOpen, onAnswerWaiting,
}: {
  next: NextMeeting | null
  /** Absent on the Meetings screen, which is already where opening it would go. */
  onOpen?: () => void
  /** Absent only where there is nowhere to send the owner to answer. */
  onAnswerWaiting?: () => void
}) {
  if (!next) return <div className="chipcard"><div className="skeleton" style={{ width: '60%' }} /></div>

  const when = next.scheduledFor ? formatWhen(next.scheduledFor) : 'Not scheduled'

  /*
    A div wrapping two controls rather than one button around everything.

    The nudge used to be a line of text inside the card's own button, which meant the one
    sentence on this screen that names something the owner has to do was the one thing
    they could not act on. A button inside a button is not valid, so the card gave up
    being one.
  */
  const Body = onOpen ? 'button' : 'div'

  return (
    <div className="chipcard">
      <Body
        className="chipcard__main"
        {...(onOpen ? { onClick: onOpen, type: 'button' as const } : {})}
      >
        <div className="chipcard__row">
          <span className="chipcard__when">{when}</span>
          <span className={`chip chip--${toneFor(next)}`}>{labelFor(next)}</span>
        </div>

        <div className="chipcard__summary">{summaryFor(next)}</div>

        {next.itemCount > 0 && (
          <div className="dots" aria-hidden>
            {Array.from({ length: Math.min(next.itemCount, 12) }, (_, i) => (
              <span key={i} className={`dot${i < next.preppedCount ? ' dot--filled' : ''}`} />
            ))}
            {next.itemCount > 12 && <span className="dim">+{next.itemCount - 12}</span>}
          </div>
        )}
      </Body>

      {next.awaitingAnswerCount > 0 && (
        onAnswerWaiting
          ? (
            <button type="button" className="chipcard__nudge chipcard__nudge--go" onClick={onAnswerWaiting}>
              <span>{waitingLabel(next.awaitingAnswerCount)}</span>
              <span className="chipcard__nudge-go" aria-hidden>Answer it ›</span>
            </button>
          )
          : <div className="chipcard__nudge">{waitingLabel(next.awaitingAnswerCount)}</div>
      )}
    </div>
  )
}

function waitingLabel(count: number): string {
  return count === 1
    ? 'One question is waiting on you'
    : `${count} questions are waiting on you`
}

function summaryFor(n: NextMeeting): string {
  if (n.chipState === 'running') return 'The board is meeting now'

  if (n.itemCount === 0) {
    // §9: a silent week is exactly when a board should be asking whether he is building a
    // business or a job. Saying "nothing on the agenda" would frame that as a gap.
    return n.overdueCommitmentCount > 0
      ? 'Nothing from you yet, so the board will set its own agenda'
      : 'Nothing from you yet. The board will bring its own questions'
  }

  const items = n.itemCount === 1 ? '1 item' : `${n.itemCount} items`
  if (n.preppedCount >= n.itemCount) return `${items} · the board has prepped all of them`
  return `${items} · the board has prepped ${n.preppedCount}`
}

function labelFor(n: NextMeeting): string {
  switch (n.chipState) {
    case 'running':  return 'Meeting now'
    case 'curating': return 'Review the agenda'
    case 'prepped':  return 'Ready'
    case 'filling':  return 'Filling'
    default:         return n.overdueCommitmentCount > 0 ? 'Follow-ups due' : 'Open'
  }
}

function toneFor(n: NextMeeting): string {
  if (n.chipState === 'running')  return 'moved'
  if (n.chipState === 'curating') return 'conditional'
  if (n.chipState === 'prepped')  return 'support'
  if (n.overdueCommitmentCount > 0) return 'dissent'
  return ''
}

function formatWhen(iso: string): string {
  const at = new Date(iso)
  const now = new Date()

  const days = Math.round(
    (new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime() -
     new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 86_400_000)

  const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

  // Weekday alone once it is more than a week out would be ambiguous, so it gets a date.
  if (days === 0) return `Today, ${time}`
  if (days === 1) return `Tomorrow, ${time}`
  if (days > 1 && days < 7) return `${at.toLocaleDateString(undefined, { weekday: 'long' })}, ${time}`
  return `${at.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}, ${time}`
}
